/**
 * Sum a numeric (or numeric‐string) field over an array,
 * optionally grouping by one key and/or filtering by a status.
 *
 * @param items      The array of records to sum over
 * @param field      The numeric field (or numeric‐string) to sum
 * @param groupKey   (optional) If provided, sums will be split out by this key’s value; otherwise all go under `"all"`
 * @param status     (optional) If provided, only items with `item.status === status` are included
 * @returns          A map from each group → sum
 */
export function sumByFieldGrouped<T extends Record<string, any>>(
  items: T[],
  field: keyof T,
  groupKey?: keyof T,
  status?: T extends { status: infer S } ? S : string
): Record<string, number> {
  const totals: Record<string, number> = {};

  for (const item of items) {
    // if a status filter was given, skip non‐matching records
    if (status !== undefined && item.status !== status) continue;

    // determine the “bucket” for this item
    const bucket = groupKey ? String(item[groupKey] ?? "unknown") : "all";

    // coerce the field value to a number
    const raw = item[field];
    let val = 0;
    if (typeof raw === "number") {
      val = raw;
    } else if (typeof raw === "string") {
      const p = parseFloat(raw);
      val = isNaN(p) ? 0 : p;
    }

    // accumulate
    totals[bucket] = (totals[bucket] || 0) + val;
  }

  return totals;
}
